<script lang="ts">
  import { _, locale } from 'svelte-i18n';
  import { getRoleLabel } from '$lib/utils/roleMapping';
  import BlurredText from './Obfuscated.svelte';
  import { dataInClear, signedIn } from '$lib/stores/auth';
  import { ArrowUpRight } from 'lucide-svelte';
  import { applicationMode } from '$lib/stores/mode';
  import { embeddingQuery } from '$lib/stores/selection';
  import { scoreBand, scorePercent } from '$lib/utils/score';
  import { createEventDispatcher } from 'svelte';
  import { mixedResults } from '$lib/stores/mixedResults';

  export let result;
  export let showSimilarPanel = false;

  // Get company metadata from mixed results
  $: companyMetadata = $mixedResults.metadata[result?.id] || null;
  $: isSimilarCompany = companyMetadata?.source === 'similar';

  const dispatch = createEventDispatcher();

  // Get all contacts
  $: contacts = result?.contacts || [];
  $: hasContacts = contacts.length > 0;

  // Determine if score should be shown
  $: showScore =
    $applicationMode === 'neighbor' ||
    ($applicationMode === 'classic' && $embeddingQuery && $embeddingQuery.trim() !== '');

  // The percentage and the bands, shared with the members' row. They were
  // written here first; they moved so the two lists cannot disagree about the
  // same company on the same search. See $lib/utils/score.
  $: scorePercentage = scorePercent(result?.quality);
  $: scoreColorClass = scoreBand(result?.quality);

  // State for description expansion
  let isExpanded = false;
  let isHovered = false;

  /**
   * Expand or collapse the card.
   *
   * # A click on a control inside the card is not a click on the card
   *
   * The card is a `<li>` with a click handler, and it holds a link and a couple
   * of buttons. Those used to guard themselves with `on:click|stopPropagation`,
   * which worked for the card and broke the link: SvelteKit intercepts link
   * clicks with a listener on `window`, so stopping propagation means the
   * router never sees it and the browser does a full document navigation
   * instead — every store emptied, the selection gone, and the app back on the
   * doors when the customer navigates back.
   *
   * So the card asks where the click came from rather than the controls
   * shouting over it. `closest` walks up from the actual target, so anything
   * inside a link or a button is that control's click and not the card's.
   */
  function toggleExpanded(event: MouseEvent) {
    if (event.target instanceof Element && event.target.closest('a, button')) return;
    isExpanded = !isExpanded;
  }

  // Get the appropriate description based on current language
  $: description = (() => {
    if ($locale === 'de' && result?.offerings_de) {
      return result.offerings_de;
    }
    return result?.offerings || result?.purpose;
  })();
</script>

<li
  class="group relative overflow-hidden rounded-2xl p-6 shadow-sm ring-1 transition-all duration-300 hover:shadow-lg {isSimilarCompany
    ? 'bg-accent-50 ring-accent-200'
    : 'bg-white ring-gray-900/5'} {result?.hydrated === false ? 'animate-pulse' : ''}"
  on:mouseenter={() => (isHovered = true)}
  on:mouseleave={() => (isHovered = false)}
  on:click={toggleExpanded}
>
  <!-- Left border gradient on hover -->
  <div
    class="absolute left-0 top-0 h-full w-1 bg-accent-400 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
  ></div>

  <div
    class="grid grid-cols-1 items-stretch gap-y-3 {hasContacts
      ? 'md:grid-cols-[80px_1fr_320px] lg:grid-cols-[80px_1fr_360px]'
      : 'md:grid-cols-[80px_1fr]'} md:gap-x-4 md:gap-y-4"
  >
    <!-- LEFT COLUMN - Mobile: inline with company info, Desktop: separate column -->
    <div class="hidden text-sm text-gray-600 md:flex md:w-auto md:flex-col md:items-center">
      <!-- SIZE - Desktop only -->
      <div class="flex flex-col items-center">
        <div class="flex flex-col items-center space-y-2">
          <div
            class="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-gray-50 to-gray-100 ring-1 ring-gray-200/50"
          >
            <svg
              class="h-5 w-5 text-gray-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.5"
                d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
              />
            </svg>
          </div>
          <span class="text-xs font-medium text-gray-600">{result.size}</span>
        </div>
      </div>
    </div>

    <!-- MIDDLE: Company Info -->
    <div class="col-span-full flex flex-col justify-between space-y-2 md:col-span-1">
      <div>
        <div class="mb-1 flex items-center gap-2">
          {#if isSimilarCompany}
            <div class="flex items-center gap-1.5 rounded-full bg-accent-100 px-2 py-0.5">
              <div
                class="h-2 w-2 animate-pulse rounded-full bg-accent-600"
                title={$_('tooltips.addedViaSimilarity')}
              ></div>
              <span class="text-xs font-semibold text-accent-700">{$_('data.similarCompany')}</span>
            </div>
          {/if}
          {#if showScore}
            <span
              class="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold transition-all duration-300 {scoreColorClass} group-hover:scale-110 group-hover:shadow-lg group-hover:ring-2 group-hover:ring-gray-900/10 group-hover:ring-offset-1"
            >
              {scorePercentage}%
            </span>
          {/if}
          <h3
            class="text-base font-semibold {isSimilarCompany
              ? 'text-accent-900'
              : 'text-gray-900'} transition-colors duration-200 group-hover:text-accent-600"
          >
            <BlurredText name={result.name} masked={!$dataInClear} />
          </h3>

          <!--
            The way into the profile.

            Only for a signed-in customer: the profile is the one screen with no
            anonymous form, so offering a visitor a link to a 401 would be
            offering them a dead end.

            A link rather than making the whole card navigate — the card's own
            click expands it, and that is the gesture people already use. It
            deliberately does *not* stop propagation: SvelteKit intercepts link
            clicks on `window`, so a stopped click is one the router never sees
            and the browser reloads the document over. `toggleExpanded` checks
            where the click came from instead.

            An ordinary client-side navigation, not a new tab: the selection
            lives in module-level stores that survive it, so Back returns to the
            results exactly as they were.
          -->
          {#if $signedIn}
            <a
              href="/company/{result.id}"
              class="inline-flex shrink-0 items-center gap-0.5 rounded-control px-1.5 py-0.5 text-xs font-medium text-gray-500 opacity-0 transition-all duration-200 hover:bg-gray-100 hover:text-accent-700 focus:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-200 group-hover:opacity-100"
              title={$_('company.openProfile')}
            >
              {$_('company.profile')}
              <ArrowUpRight class="h-3.5 w-3.5" />
            </a>
          {/if}
        </div>
        <div class="mb-4">
          <p
            class="text-sm leading-relaxed text-gray-500 transition-all duration-200 {!isExpanded &&
            !isHovered
              ? 'line-clamp-2'
              : ''}"
          >
            {description}
          </p>
        </div>
      </div>

      <div class="flex items-center justify-between">
        <div class="flex flex-wrap items-center gap-2">
          <!-- Size indicator - Mobile only, inline with badges -->
          <span
            class="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-300/10 md:hidden"
          >
            <svg class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
            {result.size}
          </span>

          <!-- Website indicator (always present) -->
          <span
            class="inline-flex items-center gap-1 rounded-full bg-accent-50 px-2.5 py-1 text-xs font-medium text-accent-700 ring-1 ring-inset ring-accent-700/10"
            title={$_('tooltips.websiteAvailable')}
          >
            <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
              />
            </svg>
          </span>

          <!-- Email indicator -->
          {#if result.email}
            <span
              class="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10"
              title={result.email}
            >
              <svg
                class="h-4 w-4 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </span>
          {/if}

          <!-- Phone indicator -->
          {#if result.phone}
            <span
              class="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-700/10"
              title={result.phone}
            >
              <svg
                class="h-4 w-4 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
            </span>
          {/if}

          <!-- LinkedIn indicator -->
          {#if result.linkedin}
            <span
              class="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-800 ring-1 ring-inset ring-blue-800/10"
              title={result.linkedin}
            >
              <svg class="h-4 w-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path
                  d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"
                />
              </svg>
            </span>
          {/if}

          <!-- Facebook indicator -->
          {#if result.facebook}
            <span
              class="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-600 ring-1 ring-inset ring-blue-600/10"
              title={result.facebook}
            >
              <svg class="h-4 w-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path
                  d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
                />
              </svg>
            </span>
          {/if}

          <!-- Instagram indicator -->
          {#if result.instagram}
            <span
              class="inline-flex items-center gap-1 rounded-full bg-accent-50 px-2.5 py-1 text-xs font-medium text-accent-700 ring-1 ring-inset ring-accent-700/10"
              title={result.instagram}
            >
              <svg class="h-4 w-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path
                  d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1112.324 0 6.162 6.162 0 01-12.324 0zM12 16a4 4 0 110-8 4 4 0 010 8zm4.965-10.405a1.44 1.44 0 112.881.001 1.44 1.44 0 01-2.881-.001z"
                />
              </svg>
            </span>
          {/if}

          <!-- Twitter indicator -->
          {#if result.twitter}
            <span
              class="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2.5 py-1 text-xs font-medium text-sky-700 ring-1 ring-inset ring-sky-700/10"
              title={result.twitter}
            >
              <svg class="h-4 w-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path
                  d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"
                />
              </svg>
            </span>
          {/if}
        </div>

        <!-- Find Similar Button -->
        <div
          on:click|stopPropagation={() =>
            dispatch('findSimilar', { id: result.id, name: result.name })}
          on:keydown={(e) =>
            e.key === 'Enter' && dispatch('findSimilar', { id: result.id, name: result.name })}
          role="button"
          tabindex="0"
          class="inline-flex cursor-pointer items-center gap-1.5 rounded-full bg-accent-50 px-3 py-1 text-xs font-medium text-accent-700 ring-1 ring-inset ring-accent-700/10 transition-all hover:bg-accent-100 hover:ring-accent-700/20 {showSimilarPanel
            ? 'bg-accent-100 ring-accent-700/20'
            : ''}"
          style="background: none !important; box-shadow: none !important;"
        >
          <svg class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
            />
          </svg>
          {$_('buttons.findSimilar')}
        </div>
      </div>
    </div>

    <!-- DIVIDER: after MIDDLE (mobile only) -->
    {#if hasContacts}
      <div
        class="col-span-full my-1 block h-px w-full bg-gradient-to-r from-transparent via-gray-200 to-transparent md:hidden"
      ></div>
    {/if}

    <!-- RIGHT: Contacts -->
    {#if hasContacts}
      <div class="md:border-l md:border-gray-100 md:pl-4">
        <div
          class="flex flex-col {contacts.length > 3
            ? 'contact-scroll max-h-48 overflow-y-auto pr-2'
            : ''}"
        >
          {#each contacts as contact, index}
            <!--
              # Spacing

              `py-2` with `mt-1` between rows, down from `py-3` and `mt-2`.
              Each row is three lines — role, name, a strip of channel icons —
              so the row already has internal height and does not need 12px of
              its own on each side as well. At the old values a card with four
              contacts spent 44px of its height on the gaps between them and
              another 36px under the last one before the card's own edge.

              The rule above the contacts on mobile loses half its margin for
              the same reason: it separates two blocks that are already
              separated by the grid's `gap-y-3`.

              # `-mx-2 px-2` on mobile, `px-4` from `md` up.

              The row's padding exists for the hover fill: without it the
              highlight would sit flush against the text. On desktop that costs
              nothing, because the contacts are their own grid column with a
              rule down its left. On mobile the grid collapses to one column,
              the company block above has no horizontal padding of its own, and
              the same `px-4` indented every contact sixteen pixels further in
              than the company it belongs to.

              Pulling the row left by exactly what it pads puts the content
              back on the company's left edge while the fill still extends past
              it — into the card's own `p-6`, which has room to spare.
            -->
            <div
              class="group/contact relative -mx-2 flex items-center gap-3 rounded-surface px-2 py-2 transition-all duration-200 hover:bg-accent-50 hover:shadow-sm md:mx-0 md:px-4 {index >
              0
                ? 'mt-1'
                : ''}"
            >
              <!-- Role Icon with gradient background -->
              <div class="relative flex-shrink-0">
                <div
                  class="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-gray-200 to-gray-300 shadow-sm"
                >
                  <svg class="h-5 w-5 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                  </svg>
                </div>
                <!-- Small status indicator -->
                <div
                  class="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-green-400 ring-1 ring-green-400/20"
                ></div>
              </div>

              <!-- Contact Info -->
              <div class="min-w-0 flex-1">
                <div class="flex items-baseline gap-2">
                  <p
                    class="overflow-hidden text-ellipsis whitespace-nowrap text-[11px] font-semibold uppercase tracking-wide text-accent-600"
                  >
                    {getRoleLabel(contact.role, $_)}
                  </p>
                </div>
                <p
                  class="text-sm font-medium text-gray-900 transition-colors duration-200 group-hover/contact:text-accent-900"
                >
                  <!-- The person, not the company: signing in does not buy them.
                       Only a contact this organisation has paid for is in clear. -->
                  <BlurredText name={contact.name} opacity={0.92} masked={!$dataInClear} />
                </p>
                <!-- Contact icons -->
                <div class="mt-1 flex flex-wrap gap-1">
                  {#if contact.email}
                    <!-- Contact has their own email - use purple envelope -->
                    <span
                      class="inline-flex items-center rounded-full bg-accent-50 p-1"
                      title={$_('tooltips.directEmail')}
                    >
                      <svg
                        class="h-3.5 w-3.5 text-accent-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    </span>
                  {:else if result.email}
                    <!-- No contact email but company has email - use blue envelope -->
                    <span
                      class="inline-flex items-center rounded-full bg-blue-50 p-1"
                      title={$_('tooltips.emailInherited')}
                    >
                      <svg
                        class="h-3.5 w-3.5 text-blue-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    </span>
                  {/if}
                  {#if contact.phone}
                    <!-- Contact has their own phone - use purple phone -->
                    <span
                      class="inline-flex items-center rounded-full bg-accent-50 p-1"
                      title={$_('tooltips.directPhone')}
                    >
                      <svg
                        class="h-3.5 w-3.5 text-accent-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                        />
                      </svg>
                    </span>
                  {:else if result.phone}
                    <!-- No contact phone but company has phone - use emerald phone -->
                    <span
                      class="inline-flex items-center rounded-full bg-emerald-50 p-1"
                      title={$_('tooltips.phoneInherited')}
                    >
                      <svg
                        class="h-3.5 w-3.5 text-emerald-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                        />
                      </svg>
                    </span>
                  {/if}
                  {#if contact.linkedin}
                    <span
                      class="inline-flex items-center rounded-full bg-blue-100 p-1"
                      title={$_('tooltips.linkedinAvailable')}
                    >
                      <svg
                        class="h-3.5 w-3.5 text-blue-700"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"
                        />
                      </svg>
                    </span>
                  {/if}
                </div>
              </div>

              <!-- Hover action icon -->
              <div
                class="transform opacity-0 transition-all duration-200 group-hover/contact:translate-x-1 group-hover/contact:opacity-100"
              >
                <svg
                  class="h-4 w-4 text-accent-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </div>
          {/each}
        </div>
      </div>
    {/if}
  </div>
</li>

<style>
  li {
    list-style-type: none;
  }

  /* Line clamp utility */
  .line-clamp-1 {
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
  }

  .line-clamp-2 {
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }

  /* Custom scrollbar for contacts list */
  :global(.contact-scroll::-webkit-scrollbar) {
    width: 6px;
  }

  :global(.contact-scroll::-webkit-scrollbar-track) {
    background: #f3f4f6;
    border-radius: 3px;
  }

  :global(.contact-scroll::-webkit-scrollbar-thumb) {
    background: #d1d5db;
    border-radius: 3px;
  }

  :global(.contact-scroll::-webkit-scrollbar-thumb:hover) {
    background: #9ca3af;
  }
</style>
